/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

/**
 * @description
 *     Each log entry retrieved from the API request should undergo the same transformations in both the filters
 *     and the logCallout controller. Not used by logCallOuts.
 * @author Alex Malitsky, Yogesh Patil, Zhiqian Liu
 */
import { Inject, Injectable } from '@angular/core';
import { StringService } from 'string-service';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';

import {
    each,
    find,
    isString,
} from 'underscore';
import moment, { Moment } from 'moment';

import {
    IBotDetectionLogRow,
    IIcapLogDisplayRow,
    IIcapLogViolation,
    IIcapNSXDefenderLogsData,
    IIcapOPSWATLogsData,
    TLogIBotClassification,
    TLogIBotManagementLog,
    TLogIDSRequestLog,
    TLogIDSRequestLogRow,
    TLogIIcapNSXDefenderLog,
    TLogIIcapOPSWATLog,
    TLogIIcapRequestLog,
    TLogIIcapViolation,
} from '../logs.types';

interface ITLogsDnsRecord {
    cname?: string;
    nsname?: string;
    addr_ip?: string;
    dns_ips?: string[];
    type?: string;
    display?: string;
}

interface ITLogsDnsResponse {
    records?: ITLogsDnsRecord[];
}

interface ITLogsResponseData {
    display: string | number;
    val: string | number;
}

interface ITLogsDnsResponseData {
    display: string;
    val: ITLogsDnsResponse;
}

interface ITLogsDnsRequestResponseRecord {
    type?: string;
    cname?: string;
    nsname?: string;
    addr_ip?: string;
    addr6_ip_str?: string;
    site_name?: string;
    vs_name?: string;
    ttl?: number;
}

// Type for HTTP Response
interface ITLogsHttpResponse {
    val: string;
    descr?: string;
}

interface ITLogsHttpVersionData {
    val: number | string;
    display?: string;
}

interface ITLogsSslCipherData {
    val: string;
    ssl_cert_type?: string;
    ssl_enc?: string;
    ssl_pfs?: string;
}

interface ITLogsDisplayData {
    val?: string;
    descr?: string;
}

interface ITLogsSignificanceData {
    val: string;
    display?: ITLogsDisplayData[];
}

interface ITLogsGrpcStatusData {
    val: number | string;
    display: number | string;
}

interface ITLogsGrpcStatusLabelData {
    val: number | string;
    display: string;
}

interface ITLogsNtlmLogData {
    ntlm_status: string;
    display: string;
}

interface ITLogsDnsOptRecord {
    options: {
        subnet_ip?: ITLogsResponseData[];
    };
}
// Type for DNS Request and Response
interface ITLogsDnsRequest {
    opt_record: ITLogsDnsOptRecord;
}

interface ITLogsIcapLog {
    action: string;
    request_logs: TLogIIcapRequestLog[];
}

interface ITLogsIcapLogData {
    action: ITLogsResponseData;
    logs: IIcapLogDisplayRow[];
}

interface ITLogsOobLog {
    ds_req_logs : TLogIDSRequestLog[];
}

interface ITLogsOobLogData {
    eventCount: number;
    logs: TLogIDSRequestLogRow[];
}

interface ITLogsBotManagementLog {
    classification: TLogIBotClassification;
    results: TLogIBotManagementLog[];
}

interface ITLogsBotManagementLogData {
    classification: {
        type: ITLogsResponseData;
        user_defined_type: ITLogsResponseData;
    };
    results: IBotDetectionLogRow[];
}

interface ITLogRow {
    adf?: boolean;
    udf?: boolean;
    id: string;
    service_engine: string;
    vcpu_id: string;
    log_id: string;
    report_timestamp: string;
    start_timestamp: string;
    dt_start: Moment;
    data_transfer_time: number;
    total_time: number;
    server_rtt: number;
    client_rtt: number;
    waf_log?: {
        latency_request_header_phase: number;
        latency_request_body_phase: number;
        latency_response_header_phase: number;
        latency_response_body_phase: number;
    };
    [key: string]: any;
}

@Injectable()
export class LogsDataTransformerService {
    constructor(
        @Inject('noValueLogLabel')
        private readonly noValueLabel: string,
        private readonly schemaService: SchemaService,
        private readonly stringService: StringService,
    ) {
    }

    public getDnsRequestResponseRecordLabel(record: ITLogsDnsRequestResponseRecord): string {
        let label = '';

        const { type } = record;

        switch (type) {
            case 'DNS_RECORD_CNAME':
                label = `Type: CNAME,  Name: ${record.cname}`;
                break;

            case 'DNS_RECORD_NS':
                label = `Type: NS, Name: ${record.nsname}`;
                break;

            case 'DNS_RECORD_A':
            case 'DNS_RECORD_AAAA': {
                const ip = record.addr_ip || record.addr6_ip_str;

                label = `Type: ${type.replace('DNS_RECORD_', '')}, IP Address: ${ip}`;

                if ('site_name' in record) {
                    label += ` (${record.site_name}:${record.vs_name})`;
                }

                break;
            }

            default:
                label = `Type: ${record.type}`;
        }

        if ('ttl' in record) {
            label += ` TTL: ${record.ttl}`;
        }

        return label;
    }

    public modifyRespCode = (code: string): ITLogsHttpResponse => {
        const res: ITLogsHttpResponse = { val: code };

        if (code) {
            const { label } =
                this.schemaService.getEnumValue('HTTPResponseCodes', `HTTP_RESPONSE_CODE_${code}`);

            if (label) {
                res.descr = label;
            }
        }

        return res;
    };

    private noValueDisplay = (value: string | number): ITLogsResponseData => {
        return {
            display: value === '' ? this.noValueLabel : value,
            val: value,
        };
    };

    public parsers: { [key: string]: any } = {
        server_ip: (str: number | string): ITLogsResponseData => {
            if (str === 0) {
                str = '';
            }

            return this.noValueDisplay(str);
        },
        vs_ip: (str: number | string): ITLogsResponseData => {
            if (str === 0) {
                str = '';
            }

            return this.noValueDisplay(str);
        },
        server_ip6: (str: number | string): ITLogsResponseData => {
            return this.noValueDisplay(str);
        },
        http_version: (str: number | string): ITLogsHttpVersionData => {
            const res: ITLogsHttpVersionData = { val: str };

            if (str || str === 0) {
                res.display = `HTTP/${str}`;
            }

            return res;
        },
        response_code: this.modifyRespCode,
        server_response_code: this.modifyRespCode,
        // @AI-Modified: 2026-01-28 - Replace vulnerable regex with safe, non-backtracking patterns
        ssl_cipher: (str: string): ITLogsSslCipherData => {
            const res: ITLogsSslCipherData = { val: str };

            // Use separate simple patterns to avoid ReDoS vulnerability
            const authMatch = str.match(/auth:(\S+)/i);
            const encMatch = str.match(/enc:(\S+)/i);
            const pfsMatch = str.match(/PFS:(\S+)/i);

            if (authMatch || encMatch || pfsMatch) {
                res.ssl_cert_type = authMatch?.[1];
                res.ssl_enc = encMatch?.[1];
                res.ssl_pfs = pfsMatch?.[1];
            }

            return res;
        },
        significance: (str: string): ITLogsSignificanceData => {
            const res: ITLogsSignificanceData = { val: str };
            const enums = this.schemaService.getEnumValues('AviDefinedFilters');
            const signReasons = str.split(', ');

            if (str) {
                res.display = signReasons.map((val: string): ITLogsHttpResponse => {
                    const res: ITLogsHttpResponse = { val };

                    const enumValue = find(enums, function(elem) {
                        return elem.label === val;
                    });

                    if (enumValue) {
                        res.descr = enumValue.description;
                    }

                    return res;
                });
            }

            return res;
        },
        ocsp_status_resp_sent: (str: string): ITLogsResponseData | undefined => {
            let res: ITLogsResponseData | undefined;

            if (str) {
                res = {
                    val: str,
                    display: this.stringService.capitalize(str.toString()),
                };
            }

            return res;
        },
        spdy_version: (str: any): ITLogsResponseData | undefined => {
            let res: ITLogsResponseData | undefined;

            if (str) {
                res = {
                    val: str,
                    display: str.slug(),
                };
            }

            return res;
        },
        grpc_status: (str: number | string): ITLogsGrpcStatusData | undefined => {
            let res: ITLogsGrpcStatusData | undefined;

            if (str !== undefined) {
                res = {
                    val: str,
                    display: str === -1 ? 'N/A' : str,
                };
            }

            return res;
        },
        grpc_status_label: (str: string): ITLogsGrpcStatusLabelData | undefined => {
            let res: ITLogsGrpcStatusLabelData | undefined;

            if (str) {
                res = {
                    val: str,
                    display: this.schemaService.getEnumValue('GRPCStatus', str).label ||
                    this.stringService.enumeration(str, 'GRPC_STATUS_CODE_'),
                };
            }

            return res;
        },
        ntlm_log: (ntlmConfig: { ntlm_status: string }): ITLogsNtlmLogData | undefined => {
            let res: ITLogsNtlmLogData | undefined;

            if (ntlmConfig) {
                res = {
                    ...ntlmConfig,
                    display:
                    this.schemaService.getEnumValue('NtlmStatus', ntlmConfig.ntlm_status).label,
                };
            }

            return res;
        },
        microservice_name: (str: number | string): ITLogsGrpcStatusData => {
            return {
                display: str || 'External-Client',
                val: str,
            };
        },
        protocol: (str: string): ITLogsGrpcStatusLabelData => {
            return {
                display: this.stringService.enumeration(str, 'PROTOCOL_'),
                val: str,
            };
        },
        dns_request: (dnsRequest: ITLogsDnsRequest): ITLogsDnsRequest => {
            const { opt_record: optRecord } = dnsRequest;
            let options;

            if (optRecord) {
                options = optRecord.options;

                if (Array.isArray(options) && options.length) {
                    const [option] = options;

                    if (option.subnet_ip) {
                        option.subnet_ip = {
                            display: option.subnet_ip,
                            val: option.subnet_ip,
                        };
                    }
                }
            }

            return dnsRequest;
        },
        dns_response: (responseObj: ITLogsDnsResponse, row: ITLogRow): ITLogsDnsResponseData => {
            const getDnsRequestResponseRecordShortLabel = (record: ITLogsDnsRecord): string =>
                record.cname || record.nsname ||
                    'addr_ip' in record && record.addr_ip ||
                    'dns_ips' in record && record.dns_ips?.join(', ') || '';

            let label = '';

            if ('records' in responseObj) {
                // dns_qtype should be set and we might have it processed
                const qType = row.dns_qtype && isString(row.dns_qtype) ?
                    row.dns_qtype : row.dns_qtype.val;

                responseObj.records?.forEach(function(record: ITLogsDnsRecord) {
                    record.display = this.getDnsRequestResponseRecordLabel(record);

                    if (!label && (!qType || record.type === qType)) {
                        label = getDnsRequestResponseRecordShortLabel(record);
                    }
                });

                // no label found by type
                if (!label) {
                    const suitableRecord =
                        find(responseObj.records ?? [], getDnsRequestResponseRecordShortLabel as any);

                    if (suitableRecord) {
                        label = getDnsRequestResponseRecordShortLabel(suitableRecord);
                    }
                }
            }

            return {
                display: label,
                val: responseObj,
            };
        },
        dns_qtype: (str: string): ITLogsResponseData => {
            return {
                display: this.stringService.enumeration(str, 'DNS_RECORD_').toUpperCase(),
                val: str,
            };
        },
        dns_etype: (str: string): ITLogsResponseData => {
            return {
                display: this.stringService.enumeration(str, 'DNS_ENTRY_'),
                val: str,
            };
        },
        icap_log: ({ action, request_logs: logs = [] }: ITLogsIcapLog): ITLogsIcapLogData => {
            return {
                // this top level action represents most severe action out of entire list
                action: {
                    display: action ? this.stringService.enumeration(action, 'ICAP_') : '-',
                    val: action,
                },
                logs: logs.map((log: TLogIIcapRequestLog) => this.transformIcapLogProps(log)),
            };
        },
        oob_log: ({ ds_req_logs: dsRequestLogs = [] }: ITLogsOobLog): ITLogsOobLogData => {
            return {
                eventCount: dsRequestLogs.length,
                logs: dsRequestLogs.map(
                    (dsRequestLog: TLogIDSRequestLog) => this.transformDSRequestLog(dsRequestLog),
                ),
            };
        },
        bot_management_log: (
            { classification, results: logs = [] }: ITLogsBotManagementLog,
        ): ITLogsBotManagementLogData => {
            return {
                classification: {
                    type: {
                        display: classification.type ?
                            this.stringService.enumeration(classification.type) : '-',
                        val: classification.type,
                    },
                    user_defined_type: {
                        display: classification.user_defined_type ?
                            this.stringService.enumeration(classification.user_defined_type) : '-',
                        val: classification.user_defined_type,
                    },
                },
                results: logs.map((result: TLogIBotManagementLog) => this.transformBotDetectionPolicyLogs(result)),
            };
        },
        bot_management_log_classification_type: (str: string): string | undefined => {
            let result;

            if (str) {
                result = this.stringService.enumeration(str);
            }

            return result;
        },
    };

    /**
     * Transform violations into usable list.
     */
    public transformIcapViolations(violations: TLogIIcapViolation[]): IIcapLogViolation[] {
        return violations.map(
            ({
                file_name: fileName,
                threat_name: threatName,
                resolution,
            }) => {
                return {
                    file_name: {
                        val: fileName,
                        display: fileName || '-',
                    },
                    threat_name: {
                        val: threatName,
                        display: threatName || '-',
                    },
                    resolution: {
                        val: resolution,
                        display: resolution ?
                            `ICAP ${this.stringService.enumeration(resolution, 'ICAP_')}` : '-',
                    },
                };
            },
        );
    }

    /**
     * Transform ICAP OPSWAT Logs into usable list.
     */
    public transformOpswatLogs(opswatLogs: TLogIIcapOPSWATLog): IIcapOPSWATLogsData {
        return {
            reason: {
                val: opswatLogs.reason,
                display: opswatLogs.reason || '-',
            },
            threat_id: {
                val: opswatLogs.threat_id,
                display: opswatLogs.threat_id || '-',
            },
            violations: opswatLogs.violations ?
                this.transformIcapViolations(opswatLogs.violations) : [],
        };
    }

    /**
     * Transform ICAP NSX Defender Logs into usable list.
     */
    public transformNsxDefenderLogs(nsxDefenderLogs: TLogIIcapNSXDefenderLog): IIcapNSXDefenderLogsData {
        return {
            score: {
                val: nsxDefenderLogs.score,
                display: nsxDefenderLogs.score || '-',
            },
            status_url: {
                val: nsxDefenderLogs.status_url,
                display: nsxDefenderLogs.status_url || '-',
            },
            task_uuid: {
                val: nsxDefenderLogs.task_uuid,
                display: nsxDefenderLogs.task_uuid || '-',
            },
        };
    }

    /**
     * Output values needed for single icapLog entry.
     */
    public transformIcapLogProps(requestLog: TLogIIcapRequestLog): IIcapLogDisplayRow {
        return {
            action: {
                val: requestLog.action,
                display: requestLog.action ?
                    this.stringService.enumeration(requestLog.action, 'ICAP_') : '-',
            },
            source_port: {
                val: requestLog.source_port,
                display: requestLog.source_port || '-',
            },
            pool_name: {
                val: requestLog.pool_name?.val,
                display: requestLog.pool_name?.val || '-',
            },
            icap_server_ip: {
                val: requestLog.icap_server_ip,
                display: requestLog.icap_server_ip || '-',
            },
            icap_headers_sent_to_server: {
                val: requestLog.icap_headers_sent_to_server,
                display: requestLog.icap_headers_sent_to_server || '-',
            },
            icap_headers_received_from_server: {
                val: requestLog.icap_headers_received_from_server,
                display: requestLog.icap_headers_received_from_server || '-',
            },
            icap_server_port: {
                val: requestLog.icap_server_port,
                display: requestLog.icap_server_port?.toString() || '-',
            },
            latency: {
                val: requestLog.latency,
                display: requestLog.latency ? `${requestLog.latency} ms` : '-',
            },
            http_method: {
                val: requestLog.http_method,
                display: requestLog.http_method ?
                    this.stringService.enumeration(requestLog.http_method, 'HTTP_METHOD_') : '-',
            },
            complete_body_sent: {
                val: requestLog.complete_body_sent,
                display: typeof requestLog.complete_body_sent === 'boolean' ?
                    this.stringService.capitalize(requestLog.complete_body_sent.toString()) : '-',
            },
            icap_absolute_uri: {
                val: requestLog.icap_absolute_uri,
                display: requestLog.icap_absolute_uri || '-',
            },
            icap_method: {
                val: requestLog.icap_method,
                display: requestLog.icap_method ?
                    this.stringService.enumeration(requestLog.icap_method, 'ICAP_METHOD_') : '-',
            },
            http_response_code: {
                val: requestLog.http_response_code,
                display: requestLog.http_response_code || '-',
            },
            modified_content_length: {
                val: requestLog.modified_content_length,
                display: requestLog.modified_content_length || '-',
            },
            /* http_response_code will be absent when icap_response_code is absent,
               except when icap_response_code is 204 */
            icap_response_code: {
                val: requestLog.icap_response_code,
                display: requestLog.icap_response_code || '-',
            },
            vendor: {
                val: requestLog.vendor,
                display: this.schemaService.getEnumValue('ICAPVendor', requestLog.vendor).label || '-',
            },
            opswat_log: requestLog.opswat_log ?
                this.transformOpswatLogs(requestLog.opswat_log) : null,

            nsx_defender_log: requestLog.nsx_defender_log ?
                this.transformNsxDefenderLogs(requestLog.nsx_defender_log) : null,
        };
    }

    /**
     * Output values needed for single Out of Band DSRequestLog entry.
     */
    public transformDSRequestLog(dsRequestLog: TLogIDSRequestLog): TLogIDSRequestLogRow {
        return {
            ds_name: {
                val: dsRequestLog.ds_name,
                display: dsRequestLog.ds_name || '-',
            },
            event: {
                val: dsRequestLog.event,
                display: this.schemaService
                    .getEnumValue('VSDataScriptEvent', dsRequestLog.event)?.label || '-',
            },
            pool_name: {
                val: dsRequestLog.pool_name?.val,
                display: dsRequestLog.pool_name?.val || '-',
            },
            pool_uuid: {
                val: dsRequestLog.pool_uuid,
                display: dsRequestLog.pool_uuid || '-',
            },
            source_port: {
                val: dsRequestLog.source_port,
                display: dsRequestLog.source_port || '-',
            },
            total_time: {
                val: +dsRequestLog.total_time,
                display: dsRequestLog.total_time ? `${dsRequestLog.total_time} ms` : '-',
            },
            server_name: {
                val: dsRequestLog.server_name?.val,
                display: dsRequestLog.server_name?.val || '-',
            },
            server_ip: {
                val: dsRequestLog.server_ip,
                display: dsRequestLog.server_ip || '-',
            },
            server_port: {
                val: dsRequestLog.server_port,
                display: dsRequestLog.server_port || '-',
            },
            servers_tried: {
                val: dsRequestLog.servers_tried,
                display: dsRequestLog.servers_tried || '-',
            },
            method: {
                val: dsRequestLog.method,
                display: dsRequestLog.method || '-',
            },
            http_version: {
                val: dsRequestLog.http_version?.val,
                display: dsRequestLog.http_version?.val || '-',
            },
            uri_path: {
                val: dsRequestLog.uri_path,
                display: dsRequestLog.uri_path || '-',
            },
            uri_query: {
                val: dsRequestLog.uri_query,
                display: dsRequestLog.uri_query || '-',
            },
            request_length: {
                val: dsRequestLog.request_length,
                display: dsRequestLog.request_length,
            },
            http_response_code: {
                val: dsRequestLog.http_response_code,
                display: dsRequestLog.http_response_code || '-',
            },
            response_length: {
                val: dsRequestLog.response_length,
                display: dsRequestLog.response_length,
            },
            headers_sent_to_server: {
                val: dsRequestLog.headers_sent_to_server,
                display: dsRequestLog.headers_sent_to_server || '-',
            },
            headers_received_from_server: {
                val: dsRequestLog.headers_received_from_server,
                display: dsRequestLog.headers_received_from_server || '-',
            },
        };
    }

    /**
     * Output values needed for single botDetectionPolicyLog entry.
     */
    public transformBotDetectionPolicyLogs(resultLog: TLogIBotManagementLog): IBotDetectionLogRow {
        return {
            component: {
                val: resultLog.component,
                display: resultLog.component ? this.stringService.enumeration(resultLog.component) : '-',
            },
            confidence: {
                val: resultLog.confidence,
                display: resultLog.component ?
                    this.stringService.enumeration(resultLog.confidence) : '-',
            },
            identification: {
                class: {
                    val: resultLog.identification.class,
                    display: resultLog.identification.class ?
                        this.stringService.enumeration(resultLog.identification.class) : '-',
                },
                identifier: {
                    val: resultLog.identification.identifier,
                    display: resultLog.identification.identifier ?
                        resultLog.identification.identifier : '-',
                },
                type: {
                    val: resultLog.identification.type,
                    display: resultLog.identification.type ?
                        this.stringService.enumeration(resultLog.identification.type) : '-',
                },
            },
            notes: resultLog.notes ? resultLog.notes.join(' ') : '-',
        };
    }

    /**
     * Transformer for log entry property.
     */
    public propOnLoad = (name: string, val: any, row?: any): any => {
        if (name in this.parsers) {
            return this.parsers[name](val, row);
        } else {
            return val;
        }
    };

    /**
     * Modify log entry on load using all defined property parsers.
     */
    public rowOnLoad = (row: ITLogRow): ITLogRow => {
        function logType(row: ITLogRow): string {
            return row.adf && 'adf' || row.udf && 'udf' || 'ndf';
        }

        row.id = [
            row.service_engine,
            row.vcpu_id,
            logType(row),
            row.log_id,
            row.report_timestamp,
        ].join('/');

        if (row.start_timestamp) {
            // L4 logs have start_timestamp
            row.dt_start = moment(row.start_timestamp);
        } else {
            // L7 logs have no start_timestamp, so we need to calculate it from report_timestamp and total_time
            row.dt_start = moment(row.report_timestamp).subtract(row.total_time, 'ms');
        }

        if (typeof row.data_transfer_time === 'undefined') { // pseudo, have no d_t_t for layer 4
            row.data_transfer_time = row.total_time - row.server_rtt - row.client_rtt;
        }

        if ('waf_log' in row) {
            const wafLogTime = [
                'latency_request_header_phase',
                'latency_request_body_phase',
                'latency_response_header_phase',
                'latency_response_body_phase',
            ].reduce(
                (acc, fieldName) => acc + Number(row.waf_log?.[fieldName]),
                0,
            );

            row.waf_log_time = Math.round(wafLogTime / 1000);
        }

        each(this.parsers, (parser, fieldName) => {
            if (fieldName in row) {
                row[fieldName] = parser(row[fieldName], row);
            }
        });

        return row;
    };
}
